import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import PublishIcon from "@mui/icons-material/Publish";
import IconButton from "@mui/material/IconButton";
import { uploadFileToS3 } from "fe-common/s3/useS3AssetsManager";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import clsx from "clsx";
//region [[ Styles ]]

const LoadingWrapper = styled("div")({
  position: "absolute",
  zIndex: 1100,
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "calc(100% - 25px)",
  height: "calc(100% - 218px)",
});

const DropZoneArea = styled("div")({
  color: "white",
  opacity: 0,
  background: "transparent",
  border: "dashed 0px",
  zIndex: 0,
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  cursor: "auto",
  height: "100%",
  "&.expanded": {
    zIndex: 1000,
    borderWidth: "3px",
    background: "#f7f7f7",
    border: "dashed 1px",
    color: "#8080808a",
    opacity: 1,
    height: "100%",
  },
  "&:focus": {
    outline: "none",
  },
  "&.disabled": {
    color: "#80808042",
    borderColor: "gainsboro",
    cursor: "not-allowed",
  },
});

const UploadImageDialog = styled(Dialog)({
  "& .upload-button": {
    position: "relative",
    zIndex: 1500,
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface CommonAssetEditorProps {
  isOpen: boolean;
  fileName: string;
  bucketFolder: string;
  onUploadFinished: (success: boolean, file: string) => void;
  assignExtensionByFile?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const CommonAssetUploader = ({
  isOpen,
  onUploadFinished,
  assignExtensionByFile,
  ...props
}: CommonAssetEditorProps) => {
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenUploadDialog] = useState(false);
  const [txtSvgValue, setTxtSvgValue] = useState("");
  const dialogRef = useRef<HTMLDivElement>();

  useEffect(() => {
    setOpenUploadDialog(isOpen);
  }, [isOpen]);

  const uploadFile = (file: File) => {
    setLoading(true);
    const fileName = assignExtensionByFile
      ? (props.fileName ||
          file.name
            .split(".")
            .slice(0, -1)
            .join(".")) +
        "." +
        file.name.split(".").pop()
      : props.fileName;

    uploadFileToS3(file, `${props.bucketFolder}/${fileName}`)
      .then((success) =>
        onUploadFinished(
          success,
          `https://assets.kahun.com/${props.bucketFolder}/${fileName}`,
        ),
      )
      .finally(() => {
        setLoading(false);
        setOpenUploadDialog(false);
      });
  };

  const onDrop = (files: File[]) => {
    uploadFile(files?.[0]);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
  });

  const handleSvgTextChanged = (e) => {
    setTxtSvgValue(e.target.value);
  };

  const createSvgFromString = () => {
    const blob = new Blob([txtSvgValue], {
      type: "image/svg+xml;charset=utf-8",
    });
    uploadFile(new File([blob], `${props.fileName}.svg`));
  };

  const handleFileWasPasted = useCallback((e) => {
    console.log("File was past!", e.target, dialogRef.current);

    if (dialogRef.current) {
      // If thats the one open, and there are files -> upload theme
      if (e.clipboardData.files.length) {
        onDrop(e.clipboardData.files);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("paste", handleFileWasPasted);
    return () => {
      window.removeEventListener("paste", handleFileWasPasted);
    };
  }, []);

  const handleClose = useCallback(() => {
    if (isOpen !== null) {
      // Controlled
      onUploadFinished(false, null);
    }
    setOpenUploadDialog(false);
  }, [isOpen]);

  return (
    <UploadImageDialog fullWidth open={openDialog} onClose={handleClose}>
      <DialogTitle>
        Drag a file here, past an svg or click to upload:
        <IconButton className={"upload-button"} onClick={open}>
          <PublishIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={dialogRef}>
        <DropZoneArea
          className={clsx(isDragActive ? "expanded" : "")}
          {...getRootProps({
            onClick: (event) => event.stopPropagation(),
          })}
        >
          <input {...getInputProps()} />
        </DropZoneArea>
        {loading && (
          <LoadingWrapper>
            <CircularProgress />
          </LoadingWrapper>
        )}
        <TextField
          disabled={loading}
          value={txtSvgValue}
          onChange={handleSvgTextChanged}
          placeholder={"Past SVG here"}
          minRows={25}
          fullWidth
          multiline
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={createSvgFromString}>
          Save
        </Button>
      </DialogActions>
    </UploadImageDialog>
  );
};
